export const DISCOUNT_DRAWER_DISPLAY_STATUS = {
  HIDDEN: 'hidden',
  APPLIED: 'applied',
  AVAILABLE: 'available',
  EMPTY: 'empty',
  FAILED: 'failed',
  LOADING: 'loading',
};

const normalizeDiscounts = (discounts) =>
  Array.isArray(discounts) ? discounts : [];

// A backend/system discount failure is one with no error code (network, 5xx,
// timeout — collapsed to an empty code by the API layer). A coded failure is a
// transaction-ineligibility failure. Single source shared by the display state
// and the eligibility-toast gating so the two never diverge.
export const isSystemDiscountFailure = (failedDiscount, errorCode) =>
  Boolean(failedDiscount) && !errorCode;

export const getDiscountDrawerDisplayState = ({
  discountsAvailable,
  discountsInactive,
  discountApplied,
  isDisabled,
  isEmptyVisible,
  isMakePayment,
  sendAmount,
  receiverAmount,
  failedDiscountRecovery,
  // DEV-1320: the active-discount-list fetch-failure state. FailureSection in
  // the discount section is driven ONLY by this (a failed getDiscountsActive
  // whose system-error modal the user has closed) — not by apply/validation or
  // eligibility failures, which surface through the eligibility toast instead.
  // Shape: { isVisible, onRetry } where onRetry refetches the active list.
  activeListFetchFailure,
  // Whether the discount state has settled for this visit (discounts loaded,
  // any default auto-validation finished, rate/calculator idle). The
  // available/empty section stays hidden on first visit until this is true, so
  // it is not shown instantly on landing. Defaults to true so callers that do
  // not gate on readiness keep their prior behaviour.
  isDiscountResultReady = true,
} = {}) => {
  const normalizedDiscountsAvailable = normalizeDiscounts(discountsAvailable);
  const normalizedDiscountsInactive = normalizeDiscounts(discountsInactive);
  const {
    canViewConditions,
    discount: failedDiscount,
    errorCode: failedErrorCode,
    isApplying: isApplyingDiscount,
    isValidating: isValidatingDiscount,
    onRetry,
    onViewConditions,
  } = failedDiscountRecovery || {};
  const {
    isVisible: isActiveListFetchFailure = false,
    onRetry: onRetryActiveListFetch,
  } = activeListFetchFailure || {};

  const hasAppliedDiscount = Boolean(discountApplied);
  const hasAvailableDiscounts = normalizedDiscountsAvailable.length > 0;
  const hasInactiveDiscounts = normalizedDiscountsInactive.length > 0;
  const isLoadingVisible = Boolean(isApplyingDiscount || isValidatingDiscount);
  // A failed discount is a post-validation result. It must not be evaluated
  // while discount validation/apply is still running (that would surface a
  // stale or premature failure); it is only shown once validation has
  // completed.
  const isDiscountValidationRunning = Boolean(
    isApplyingDiscount || isValidatingDiscount,
  );
  // Retained for the eligibility-toast gating consumers (still exported below):
  // a coded failure is an ineligibility failure surfaced through the toast; a
  // no-code apply/validation failure is a system failure. This no longer drives
  // FailureSection — DEV-1320 retargets that to active-list fetch failures only.
  const systemDiscountFailure = isSystemDiscountFailure(
    failedDiscount,
    failedErrorCode,
  );
  // DEV-1320: FailureSection appears only for an active-list fetch failure (its
  // modal closed), never for apply/validation/eligibility errors. Still gated on
  // validation not running so it cannot paint over an in-flight apply.
  const isFailureVisible = Boolean(
    !isDiscountValidationRunning && isActiveListFetchFailure,
  );
  // DEV-1320: a fetch failure is not an empty result. Exclude it so the failure
  // state wins — DiscountAvailableSection checks its empty branch before the
  // failure branch, so a truthy isEmpty here would shadow FailureSection (and its
  // retry) with the "no available offer" empty copy. This also matches the status
  // logic below, which already prioritises FAILED over EMPTY.
  const isEmpty = Boolean(
    !isLoadingVisible &&
      isDiscountResultReady &&
      isEmptyVisible &&
      !isFailureVisible &&
      !hasAvailableDiscounts &&
      !hasInactiveDiscounts,
  );
  const isAvailableSectionVisible = Boolean(
    !isLoadingVisible &&
      !hasAppliedDiscount &&
      isDiscountResultReady &&
      (hasAvailableDiscounts ||
        hasInactiveDiscounts ||
        isEmptyVisible ||
        isFailureVisible),
  );
  const isAppliedSectionVisible = Boolean(!isLoadingVisible && hasAppliedDiscount);
  // The discount section is hidden whenever the primary send or receive amount
  // is empty or 0 -- there is no meaningful transaction to discount yet. This
  // overrides every other state (loading/applied/available/empty). The rule is
  // orthogonal: it only applies to an amount the caller actually supplies
  // (a cleared field arrives as '' -> Number('') === 0 -> hidden); an omitted
  // amount (undefined) leaves prior behaviour untouched.
  const isEmptyAmountHidden = Boolean(
    (sendAmount !== undefined && !Number(sendAmount)) ||
      (receiverAmount !== undefined && !Number(receiverAmount)),
  );
  const isDisabledHidden = Boolean(!hasAppliedDiscount && isDisabled);
  const isMakePaymentHidden = Boolean(
    !hasAppliedDiscount && isMakePayment && !isEmptyVisible,
  );
  // While a discount is validating/applying, keep the drawer visible so its
  // loading background (#DFE0FF4D) shows. The disabled/make-payment hide rules
  // only apply once validation has completed.
  const isDrawerHidden = Boolean(
    isEmptyAmountHidden ||
      (!isLoadingVisible &&
        ((!isAvailableSectionVisible && !isAppliedSectionVisible) ||
          isDisabledHidden ||
          isMakePaymentHidden)),
  );
  const availableQuantity = normalizedDiscountsAvailable.length;
  const canRetry = Boolean(onRetry);
  // DEV-1320: FailureSection's retry refetches the active list.
  const canRetryActiveListFetch = Boolean(onRetryActiveListFetch);
  const canViewFailedDiscountConditions = Boolean(
    canViewConditions && onViewConditions,
  );

  let status = DISCOUNT_DRAWER_DISPLAY_STATUS.HIDDEN;

  if (isEmptyAmountHidden) {
    status = DISCOUNT_DRAWER_DISPLAY_STATUS.HIDDEN;
  } else if (isLoadingVisible) {
    status = DISCOUNT_DRAWER_DISPLAY_STATUS.LOADING;
  } else if (isDrawerHidden) {
    status = DISCOUNT_DRAWER_DISPLAY_STATUS.HIDDEN;
  } else if (isAppliedSectionVisible) {
    status = DISCOUNT_DRAWER_DISPLAY_STATUS.APPLIED;
  } else if (isFailureVisible) {
    status = DISCOUNT_DRAWER_DISPLAY_STATUS.FAILED;
  } else if (isEmpty) {
    status = DISCOUNT_DRAWER_DISPLAY_STATUS.EMPTY;
  } else if (isAvailableSectionVisible) {
    status = DISCOUNT_DRAWER_DISPLAY_STATUS.AVAILABLE;
  }

  return {
    status,
    discountsAvailable: normalizedDiscountsAvailable,
    discountsInactive: normalizedDiscountsInactive,
    discountApplied,
    isAvailableSectionVisible,
    isAppliedSectionVisible,
    isDrawerHidden,
    isEmpty,
    isFailureVisible,
    isSystemDiscountFailure: systemDiscountFailure,
    isDiscountResultReady,
    isLoadingVisible,
    isLoadingSectionVisible: isLoadingVisible,
    isDisabledHidden,
    isMakePaymentHidden,
    availableQuantity,
    failedDiscount,
    isApplyingDiscount: Boolean(isApplyingDiscount),
    isValidatingDiscount: isLoadingVisible,
    canRetry,
    canViewConditions: canViewFailedDiscountConditions,
    onRetry,
    onViewConditions,
    availableSectionAvailability: {
      quantity: availableQuantity,
      isEmpty,
    },
    // DEV-1320: the failure props consumed by DiscountAvailableSection's
    // FailureSection. Driven by the active-list fetch failure; the retry
    // refetches that list. No per-discount code / applying state applies here.
    availableSectionFailure: {
      isVisible: isFailureVisible,
      isApplying: false,
      isValidating: isLoadingVisible,
      canRetry: canRetryActiveListFetch,
      onRetry: onRetryActiveListFetch,
    },
  };
};
